import { useRef, type ReactNode } from "react";

import { Button, type ButtonProps } from "@/components/ui/button";
import { useOrderFileReader } from "@/hooks/use-order-file-reader";
import type { LoadedFile } from "@/lib/order-import/detect-columns";

/** What the file input offers. Old binary .xls is listed only to give a clear error. */
const IMPORT_FILE_ACCEPT = ".xlsx,.xls,.csv,.txt";

interface ImportFileButtonProps extends Pick<ButtonProps, "variant" | "size" | "className"> {
  onLoaded: (file: LoadedFile) => void;
  children: ReactNode;
}

/** Button + hidden file input for an order file. */
export function ImportFileButton({ onLoaded, children, ...buttonProps }: ImportFileButtonProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const { reading, read } = useOrderFileReader(onLoaded);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept={IMPORT_FILE_ACCEPT}
        className="sr-only"
        tabIndex={-1}
        aria-hidden="true"
        data-testid="order-import-file"
        onChange={(e) => {
          const file = e.target.files?.[0];
          // Cleared so picking the same file again (after fixing it) fires change again.
          e.target.value = "";
          if (file) void read(file);
        }}
      />
      <Button
        type="button"
        {...buttonProps}
        disabled={reading}
        onClick={() => inputRef.current?.click()}
      >
        {reading ? "Читаем файл…" : children}
      </Button>
    </>
  );
}
